import React, {createContext, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import moment from 'moment/moment';
import * as _ from 'lodash';
import {FormikProvider, useFormik} from 'formik';
import {formattingPrice} from '../../_common/utils/format';
import * as columnFormatters from '../../_common/column-formatters';
import * as actions from '../_redux/transfersByAgentActions';
import {monthsOptions} from '../../_common/constants/options';
import {useTransfersByAgentContext} from './TransfersByAgentContext';
import {getAmountPercent, getPaymentTypeName, isNewPaycheck, isNullOrEmpty} from '../utils';

const PopupCreateTransferContext = createContext({});
export const usePopupCreateTransferContext = () => useContext(PopupCreateTransferContext);

export const PopupCreateTransferContextProvider = ({children, cellContent}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const getFloatValue = (value) => value ? parseFloat(value) : 0;
  const {
    unpaidReceiptLines,
    agentId,
    getTransfersByAgent,
    currentLastPaycheck,
    transfersLines
  } = useTransfersByAgentContext();
  const columns = useMemo(() => [
    {
      accessor: 'checkbox',
      Header: ''
    },
    {
      accessor: 'receiptID',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptID',
        defaultMessage: 'Receipt ID'
      })
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-customer',
        defaultMessage: 'Customer'
      })
    },
    {
      accessor: 'receiptDate',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptDate',
        defaultMessage: 'Receipt date'
      })
    },
    {
      accessor: 'paymentType',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-paymentType',
        defaultMessage: 'Payment type'
      })
    },
    {
      accessor: 'amount',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-amount',
        defaultMessage: 'Amount'
      })
    },
    {
      accessor: 'vat',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-vat',
        defaultMessage: 'VAT'
      })
    },
  ], []);
  const mappedLines = unpaidReceiptLines.map(item => {
    return {vatIncluded: true, ...item};
  });
  const tableData = useMemo(() => [
    ...mappedLines.map((item, index) => {
      const customer = [
        _.get(item, 'receipt.customer._id', ''),
        '-',
        _.get(item, 'receipt.customer.name.first', ''),
        _.get(item, 'receipt.customer.name.last', '')
      ].filter(Boolean).join(' ');
      const amount = item.amount && typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount;
      const amountValue = formattingPrice(amount, true);
      return {
        _id: item._id,
        checkbox: columnFormatters.TransfersCheckboxColumnFormatter(`table[${index}]receipt-${item._id}`),
        receiptID: columnFormatters.TransfersNormalColumnFormatter(item._id),
        customer: columnFormatters.TransfersNormalColumnFormatter(customer),
        receiptDate: columnFormatters.TransfersDateColumnFormatter(item.dueDate),
        paymentType: columnFormatters.TransfersNormalColumnFormatter(getPaymentTypeName(item.paymentType)),
        amount: columnFormatters.TransfersNormalColumnFormatter(amountValue),
        vat: columnFormatters.TransfersCheckboxColumnFormatter(`table[${index}]receipt-${item._id}-vatIncluded`),
      };
    })
  ], [unpaidReceiptLines]);

  const getInitialCommission = () => {
    if (transfersLines && transfersLines.length > 0) {
      const calcCommission = Math.abs(_.get(transfersLines[0], 'calculation.commission', 0));
      const socialCommission = Math.ceil(_.get(transfersLines[0], 'socialParams.commission', 0));
      const socialCommissionValue = _.get(transfersLines[0], 'socialParams.commission', 0);
      if (socialCommission) {
        return {
          value: socialCommission.toString().includes('%') ? socialCommission.toString().replace('%', '') : socialCommissionValue,
          type: socialCommission.toString().includes('%') || socialCommission !== calcCommission ? 'commission-percent' : 'commission-amount',
        };
      } else return {
        value: '5',
        type: 'commission-percent',
      };
    } else return {
      value: '5',
      type: 'commission-percent',
    };
  };

  const getInitialMonthValue = () => {
    const isFirstHalf = moment().date() <= 15;
    const month = moment().month() + (isFirstHalf ? 1 : 2);

    return monthsOptions.filter(item => item.value === month)[0];
  };

  const initialValues = {
    expensesWithVat: 0,
    vat: 0,
    expensesWithoutVat: 0,
    exchangeFee: 0,
    pensionManagementCommission: 0,
    incomeTaxOffset: 0,
    commission: getInitialCommission().value,
    commissionValueType: getInitialCommission().type,
    creditCardCommission: 0,
    creditCardCommissionValueType: 'creditCardCommission-amount',
    compensations: 0,
    compensationsValueType: 'compensations-amount',
    employerEducationFund: 0,
    employerEducationFundValueType: 'employerEducationFund-amount',
    employerProvidentFund: 0,
    employerProvidentFundValueType: 'employerProvidentFund-amount',
    employerMandatoryPension: 0,
    employerMandatoryPensionValueType: 'employerMandatoryPension-amount',
    employeeEducationFund: 0,
    employeeEducationFundValueType: 'employeeEducationFund-amount',
    employeeProvidentFund: 0,
    employeeProvidentFundValueType: 'employeeProvidentFund-amount',
    employeeMandatoryPension: 0,
    employeeMandatoryPensionValueType: 'employeeMandatoryPension-amount',
    advancePayment: 0,
    nationalInsurance: 0,
    nationalInsuranceDebt: 0,
    supplierExpensesRefund: 0,
    additionalCommission: 0,
    year: {label: `${moment().year()}`, value: moment().year()},
    month: getInitialMonthValue(),
    splits: {label: '1', value: 1},
    table: mappedLines.map((item) => {
      return {
        _id: item._id,
        [`receipt-${item._id}`]: true,
        [`receipt-${item._id}-vatIncluded`]: item.vatIncluded,
        amount: item.amount
      };
    }),
    customLines: [],
    adminNotes: '',
    allReceiptsChecked: true
  };

  const formik = useFormik({
    initialValues: {...initialValues},
    onSubmit: () => {
    },
    validate: (values) => {
      let errors = {};
      const {commission} = values;
      if (isNullOrEmpty(commission)) errors.commission = 'Required';

      return errors;
    },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const setAllReceiptsChecked = () => {
    formik.setFieldValue('allReceiptsChecked', !formik.values.allReceiptsChecked);
    formik.values.table.map((item, index) => {
      if (item) {
        const {_id} = item;
        formik.setFieldValue(`table[${index}].[receipt-${_id}]`, !formik.values.allReceiptsChecked);
      }
    });
  };

  const handleCreate = () => {
    const {values} = formik;

    const preparedData = {
      lines: values.table,
      agentID: agentId ? parseInt(agentId) : 0,
      datetime: new Date(),
      formValues: {
        customLines: values.customLines,
        vatIncludedCustomLines: values.customLines
          .map((item, index) => item.vatIncluded ? index : 'notIncluded')
          .filter(item => item !== 'notIncluded')
          .reduce((o, key) => ({...o, [key]: true}), {}),
        selectedLines: values.table
          .filter(item => item[`receipt-${item._id}`])
          .map(item => item._id)
          .reduce((o, key) => ({...o, [key]: true}), {}),
        vatIncludedLines: values.table
          .filter(item => item[`receipt-${item._id}-vatIncluded`])
          .map(item => item._id)
          .reduce((o, key) => ({...o, [key]: true}), {}),
        socialParams: {
          pensionManagementCommission: getFloatValue(values.pensionManagementCommission),
          nationalInsurance: getFloatValue(values.nationalInsurance),
          additionalCommission: getFloatValue(values.additionalCommission),
          commission: getAmountPercent(values, 'commissionValueType', 'commission'),
          compensations: getAmountPercent(values, 'compensationsValueType', 'compensations'),
          employeeEducationFund: getAmountPercent(values, 'employeeEducationFundValueType', 'employeeEducationFund'),
          employeeProvidentFund: getAmountPercent(values, 'employeeProvidentFundValueType', 'employeeProvidentFund'),
          employeeMandatoryPension: getAmountPercent(values, 'employeeMandatoryPensionValueType', 'employeeMandatoryPension'),
          employerEducationFund: getAmountPercent(values, 'employerEducationFundValueType', 'employerEducationFund'),
          employerProvidentFund: getAmountPercent(values, 'employerProvidentFundValueType', 'employerProvidentFund'),
          employerMandatoryPension: getAmountPercent(values, 'employerMandatoryPensionValueType', 'employerMandatoryPension'),
        },
        transferParams: {
          advancePayment: getFloatValue(values.advancePayment),
          creditCardCommission: getAmountPercent(values, 'creditCardCommissionValueType', 'creditCardCommission'),
          exchangeFee: getFloatValue(values.exchangeFee),
          expensesWithVat: getFloatValue(values.expensesWithVat),
          expensesWithoutVat: getFloatValue(values.expensesWithoutVat),
          incomeTaxOffset: getFloatValue(values.incomeTaxOffset),
          nationalInsuranceDebt: getFloatValue(values.nationalInsuranceDebt),
          supplierExpensesRefund: getFloatValue(values.supplierExpensesRefund),
        },
        paycheck: {
          date: {
            year: values.year.value,
            month: values.month.value
          },
          splits: values.splits.value
        },
        preferences: {
          adminNotes: values.adminNotes || ''
        }
      },
    };

    dispatch(actions.createTransfer(preparedData));
    getTransfersByAgent();
    formik.resetForm();
  };

  const updateVat = vat => formik.setFieldValue('vat', vat);

  return (
    <PopupCreateTransferContext.Provider
      value={{
        formik,
        columns,
        tableData,
        cellContent,
        newPaycheck: isNewPaycheck(formik.values, currentLastPaycheck),
        handleCreate,
        setAllReceiptsChecked,
        updateVat
      }}
    >
      <FormikProvider value={formik}>{children}</FormikProvider>
    </PopupCreateTransferContext.Provider>
  );
};